import type {
  AriaAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react'
import React, { Children, cloneElement, isValidElement } from 'react'
import TextButton from '../Button/Button'
import type { ButtonSize, ButtonVariant } from '../Button/Button'
import { useDisposables } from '../hooks/useDisposables'
import { useSyncRefs } from '../hooks/useSyncRefs'
import IconButton from '../IconButton/IconButton'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { Focus } from '../utilities/calculateActiveIndex'
import { Keys } from '../utilities/keyboard'
import { toDataSet } from '../utilities/toDataSet'
import { ActionTypes } from './Actions'
import { useMenu } from './Menu'

export type ButtonProps =
  | {
      children?: ReactNode & { type?: { uiName?: string } }
      color?: MenuButtonColor
      dataSet?: Record<string, string | undefined>
      disabled?: boolean
      endAdornment?: typeof SvgIcon
      icon: true
      label: string
      size?: ButtonSize
      startAdornment?: typeof SvgIcon
      tabIndex?: number
      variant?: ButtonVariant
    }
  | {
      children?: ReactNode & { type?: { uiName?: string } }
      color?: MenuButtonColor
      dataSet?: Record<string, string | undefined>
      disabled?: boolean
      endAdornment?: typeof SvgIcon
      icon?: false
      label?: never
      size?: ButtonSize
      startAdornment?: typeof SvgIcon
      tabIndex?: number
      variant?: ButtonVariant
    }

interface ButtonPassThruProps {
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  color: MenuButtonColor
  dataSet?: Record<string, string | undefined>
  disabled: boolean
  endAdornment?: typeof SvgIcon
  id: string
  onClick: MouseEventHandler<HTMLButtonElement>
  onKeyDown: KeyboardEventHandler<HTMLButtonElement>
  ref: Ref<HTMLButtonElement>
  size: ButtonSize
  startAdornment?: typeof SvgIcon
  tabIndex: number
}

type MenuButtonColor =
  | 'danger'
  | 'default'
  | 'inverse'
  | 'primary'
  | 'secondary'

function Button(
  {
    children,
    color = 'default',
    dataSet,
    disabled = false,
    endAdornment,
    icon = false,
    label = '',
    size = 'small',
    startAdornment,
    tabIndex = 0,
    variant = 'text',
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>,
): JSX.Element {
  const d = useDisposables()
  const [state, dispatch] = useMenu()
  const buttonRef = useSyncRefs(state.buttonRef, ref)

  const props: ButtonPassThruProps = {
    'aria-controls': `coconut-menu-${state.id}`,
    'aria-expanded': state.show ? 'true' : undefined,
    'aria-haspopup': 'true',
    color,
    disabled,
    ...(icon || state.headless ? {} : { endAdornment }),
    id: `coconut-menu-button-${state.id}`,
    onClick: (event) => {
      if (state.show) {
        dispatch({ type: ActionTypes.CloseMenu })
        d.nextFrame(() => {
          if (state.headless) {
            state.buttonRef.current?.parentElement?.parentElement?.focus({
              preventScroll: true,
            })
          } else {
            if (state.focusRef?.current) {
              state.focusRef.current?.focus({ preventScroll: true })
            } else {
              state.buttonRef.current?.focus({ preventScroll: true })
            }
          }
        })
      } else {
        event.preventDefault()
        dispatch({ type: ActionTypes.OpenMenu })
        d.nextFrame(() => {
          state.itemsRef.current?.focus({ preventScroll: true })
        })
      }
    },
    onKeyDown: (event) => {
      if (event.repeat) {
        return
      }

      switch (event.key) {
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault()
          dispatch({ type: ActionTypes.OpenMenu })
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
            dispatch({ type: ActionTypes.GoToItem, focus: Focus.First })
          })
          break

        case Keys.ArrowUp:
          event.preventDefault()
          dispatch({ type: ActionTypes.OpenMenu })
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
            dispatch({ type: ActionTypes.GoToItem, focus: Focus.Last })
          })
      }
    },
    ref: buttonRef,
    size,
    ...(icon || state.headless ? {} : { startAdornment }),
    tabIndex,
  }

  if (icon) {
    return (
      <IconButton aria-label={label} dataSet={dataSet} {...props}>
        {children}
      </IconButton>
    )
  }

  return state.headless ? (
    <button
      className="w-full h-full text-left focus:outline-none"
      type="button"
      {...toDataSet(dataSet)}
      {...props}
    >
      <div className="flex items-center">
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            // @ts-expect-error The only current use case here is for the
            // Table component where we use the `headless` prop in order
            // to pass the `MenuHeader` component into `Menu.Button`.
            // The types required for Typescript to not complain are
            // completely internal to that file, so we will be
            // ignoring this error.
            //
            // @see file://./../Table/Head.tsx
            return cloneElement(child, { show: state.show })
          }

          return child
        })}
      </div>
    </button>
  ) : (
    <TextButton dataSet={dataSet} variant={variant} {...props}>
      {children}
    </TextButton>
  )
}

export default Button
